import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';

import LogRocket from 'logrocket';
import PropTypes from 'prop-types';

import {
  ERROR_SAVE_DRAFT,
  QUESTION_TYPES,
  RESEARCH_OBJECTIVES_MAX_LENGTH,
  SURVEY_TYPES,
} from '@api/instant_survey';

import { shareInstantSurveyWithClientGroup, shareInstantSurveyWithUsers } from '@api/surveys';

import { GATES } from '@constants';
import {
  ButtonText,
  Card,
  HelpIcon,
  Input,
  MinusIcon,
  OverlayTriggerTooltip,
  Pill,
  PlusIcon,
  SaveIcon,
  ShareIcon,
  Stepper,
  TextArea,
  toast,
  Toolbar,
} from '@utilities';

import { getCurrentSamplePrice } from '../../utilities/helpers';
import { payWithCompletes, payWithCredits } from '../../../Checkout/utilities/constants';

import ModalSaveAsDraft from '../ModalSaveAsDraft';
import ModalShare from '../../../../components/ModalShare';
import ModalTitleRequired from '../ModalTitleRequired';
import { SurveyContext } from '../../utilities/context';

import ModalLaunchMethodType from './components/ModalLaunchMethodType';

import styles from './_index.module.scss';

const SurveyToolbar = ({ isSaveAsDraftDisabled, tools = [] }) => {
  const {
    activeGroups,
    activeQuestions,
    activeStep,
    currentQuestionCount,
    currentQuestionnairePrice,
    insightsDocument,
    insightsDocumentType,
    instantSurveyType,
    isAutoSaving,
    isLoadingSubscription,
    isReadOnly,
    isResearchObjectivesTooLong,
    isSaveAsDraftOpen,
    isSavingAsDraft,
    isSubscribed,
    launchMethod,
    maxQuestions,
    persistSurveyDraft,
    setActiveStep,
    setIsSaveAsDraftOpen,
    setIsSavingAsDraft,
    setLaunchMethod,
    subscription,
    subscriptionError,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);
  const [isLaunchMethodModalOpen, setIsLaunchMethodModalOpen] = useState(false);

  const [isSurveyTitleModalOpen, setIsSurveyTitleModalOpen] = useState(false);
  const [showModalShare, setShowModalShare] = useState(false);
  const [showResearchObjectiveEdit, setShowResearchObjectiveEdit] = useState(false);
  const { client } = useStatsigClient();
  const canShowResearchObjectives = client.checkGate(GATES.RESEARCH_OBJECTIVES);

  const currentSamplePrice = useMemo(
    () =>
      getCurrentSamplePrice({
        samplePricing: insightsDocumentType?.samplePricing,
        survey,
      }),
    [survey]
  );

  const handleConfirmMethod = (method) => {
    setLaunchMethod(method);
    setIsLaunchMethodModalOpen(false);
  };

  const currentTotalResponses = activeGroups.reduce((acc, group) => acc + group.quotaLimit, 0);
  const hasMaxDiffQuestion = activeQuestions.some(
    ({ type }) => type === QUESTION_TYPES.MAX_DIFF.value
  );
  const hasTurfQuestion = activeQuestions.some(({ type }) => type === QUESTION_TYPES.TURF.value);
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const isSubscriptionAvailable =
    import.meta.env.VITE_LAUNCH_METHOD &&
    isSubscribed &&
    !isLoadingSubscription &&
    !subscriptionError &&
    subscription?.hasStarted === true;

  const advancedMethodologyCompletesCost = 300;
  const advancedMethodologyCreditCost = 1;
  const completesTotal =
    currentTotalResponses +
    (currentQuestionnairePrice?.questions >= 16 ? 100 : 0) +
    (hasMaxDiffQuestion ? advancedMethodologyCompletesCost : 0) +
    (hasTurfQuestion ? advancedMethodologyCompletesCost : 0);

  const questionnaireCredits = isVideoSurvey ? 0 : (currentQuestionnairePrice?.amount ?? 1);
  const creditsTotal =
    (currentSamplePrice?.amount ?? 0) +
    (hasMaxDiffQuestion ? advancedMethodologyCreditCost : 0) +
    (hasTurfQuestion ? advancedMethodologyCreditCost : 0) +
    questionnaireCredits;

  const navigate = useNavigate();
  const handleOpenSaveAsDraft = () => {
    if (!survey.title.trim()) {
      setIsSurveyTitleModalOpen(true);
    } else {
      setIsSaveAsDraftOpen(true);
    }
  };
  const handleSaveAndExit = async () => {
    setIsSavingAsDraft(true);
    try {
      await persistSurveyDraft({ reason: 'manual' });
    } catch (error) {
      toast.error(ERROR_SAVE_DRAFT);
      setIsSavingAsDraft(false);
      return;
    }

    LogRocket.track('InstantSurvey_SaveAsDraft', {
      country: survey.country,
      groups: survey.groups.length,
      questions: survey.questions.length,
      ...(survey.surveyTemplateId && { template: survey.surveyTemplateId }),
      type: instantSurveyType.id,
    });
    setIsSavingAsDraft(false);
    navigate({
      pathname: '/dashboard/workspace/surveys',
      search: new URLSearchParams({ filters: 'is_draft_item' }).toString(),
    });
  };

  return (
    <>
      <Card className={styles['survey-toolbar']} padding="medium">
        <div>
          <Toolbar
            tools={[
              ...tools,
              ...(insightsDocument?.id && !isReadOnly
                ? [
                    <OverlayTriggerTooltip content="Share" key="share">
                      <button data-testid="share-button" onClick={() => setShowModalShare(true)}>
                        <ShareIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(!isReadOnly
                ? [
                    <OverlayTriggerTooltip
                      content={isAutoSaving ? 'Auto-saving in progress...' : 'Save Draft and Close'}
                      key="save-as-draft"
                    >
                      <button
                        data-testid="save-draft-button"
                        disabled={isSaveAsDraftDisabled || isAutoSaving}
                        onClick={handleOpenSaveAsDraft}
                      >
                        <SaveIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(instantSurveyType.helpUrl
                ? [
                    <OverlayTriggerTooltip aria-label="Help" content="Help" key="help">
                      <a
                        data-testid="help-link-button"
                        href={instantSurveyType.helpUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <HelpIcon />
                      </a>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
            ]}
          >
            <Input
              className={styles['survey-toolbar-input']}
              isDisabled={isReadOnly}
              maxLength={255}
              onBlur={(title) => updateSurvey({ title: title.trim() }, { markDirty: true })}
              onChange={(title) => updateSurvey({ title }, { markDirty: true })}
              placeholder="Name your survey (e.g., Heavy vs Light Walmart coffee buyers)"
              value={survey.title}
            />
          </Toolbar>
          {canShowResearchObjectives && (!isReadOnly || survey.researchObjectives?.length > 0) && (
            <div className={styles['survey-toolbar-research-objectives']}>
              {showResearchObjectiveEdit ? (
                <div>
                  <div className={styles['survey-toolbar-research-objectives-edit']}>
                    <TextArea
                      className={styles['survey-toolbar-research-objectives-textarea']}
                      error={
                        isResearchObjectivesTooLong
                          ? `Research objective is too long, cannot exceed ${RESEARCH_OBJECTIVES_MAX_LENGTH} characters.`
                          : null
                      }
                      isDisabled={isReadOnly}
                      onBlur={() => {
                        updateSurvey(
                          { researchObjectives: survey.researchObjectives?.trim() },
                          { markDirty: true }
                        );
                        setShowResearchObjectiveEdit(false);
                      }}
                      onChange={(research_objectives) =>
                        updateSurvey(
                          { researchObjectives: research_objectives },
                          { markDirty: true }
                        )
                      }
                      placeholder="Enter the research objective for this survey."
                      value={survey.researchObjectives || ''}
                    />
                  </div>
                  <div>
                    <ButtonText
                      onClick={() => {
                        setShowResearchObjectiveEdit(false);
                      }}
                    >
                      <MinusIcon />
                      <span>
                        Hide <strong>Objective</strong>
                      </span>
                    </ButtonText>
                  </div>
                </div>
              ) : (
                <ButtonText onClick={() => setShowResearchObjectiveEdit(true)}>
                  <PlusIcon />
                  <span>
                    Show <strong>Objective</strong>
                  </span>
                </ButtonText>
              )}
            </div>
          )}
        </div>

        <div className={styles['survey-toolbar-info']}>
          <div>
            <p>
              <strong>{activeGroups.length}</strong> Quota Group
              {activeGroups.length > 1 ? 's' : ''}
            </p>
            <p>
              <strong>{currentTotalResponses}</strong> Total Responses
            </p>
            <p>
              <strong>{currentQuestionCount + '/' + maxQuestions}</strong>{' '}
              {`Question${maxQuestions === 1 ? '' : 's'}`}
            </p>
          </div>
          <div>
            {isSubscriptionAvailable ? (
              <>
                <button
                  className={styles['survey-toolbar-launch-method-button']}
                  disabled={isReadOnly}
                  onClick={() => setIsLaunchMethodModalOpen(true)}
                >
                  {isReadOnly ? 'Launched with' : 'Launch with'}
                </button>
                &nbsp;
                <span>
                  <strong>
                    {launchMethod === payWithCompletes.key
                      ? `${completesTotal} Completes`
                      : launchMethod === payWithCredits.key &&
                          creditsTotal !== undefined &&
                          !isNaN(creditsTotal)
                        ? `${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`
                        : 'N/A'}
                  </strong>
                </span>
              </>
            ) : (
              <>
                Total Cost:&nbsp;
                {currentSamplePrice ? (
                  <Pill
                    isCompleted
                    value={`${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`}
                  />
                ) : (
                  <span>N/A</span>
                )}
              </>
            )}
          </div>
        </div>
        <Stepper
          activeIndex={activeStep}
          navigationType="linear"
          onChange={(index) => setActiveStep(index)}
          steps={[
            {
              isComplete: activeStep > 0,
              label: 'Select Panelists',
            },
            {
              isComplete: survey.questions.length >= 1,
              label: 'Create Questionnaire',
            },
            {
              label: 'Review Survey',
            },
          ]}
        />
      </Card>
      {isSaveAsDraftOpen && (
        <ModalSaveAsDraft
          isActive
          isSaving={isSavingAsDraft}
          onClose={() => setIsSaveAsDraftOpen(false)}
          onSubmit={handleSaveAndExit}
        />
      )}
      {isSurveyTitleModalOpen && (
        <ModalTitleRequired onClose={() => setIsSurveyTitleModalOpen(false)} />
      )}
      {isLaunchMethodModalOpen && (
        <ModalLaunchMethodType
          creditsTotal={creditsTotal}
          isOpen={isLaunchMethodModalOpen}
          launchMethod={launchMethod}
          onClose={() => setIsLaunchMethodModalOpen(false)}
          onConfirm={handleConfirmMethod}
          setLaunchMethod={setLaunchMethod}
        />
      )}
      {showModalShare && insightsDocument?.id && (
        <ModalShare
          hasAllColleagues
          hasFindColleagues
          hasSharePublicly
          ids={[Number(insightsDocument.id)]}
          isSurvey
          setShowModalShare={setShowModalShare}
          shareItemWithClients={shareInstantSurveyWithClientGroup}
          shareItemWithUsers={shareInstantSurveyWithUsers}
        />
      )}
    </>
  );
};

SurveyToolbar.propTypes = {
  isSaveAsDraftDisabled: PropTypes.bool,
  tools: PropTypes.array,
};

export default SurveyToolbar;
